import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Users, EnvelopeSimple, Briefcase, Handshake, SignOut, Trash, ChartLine, Globe, Clock, ArrowLeft, Key, Trophy, Newspaper, MapPin, ChatCircle, Gear, Image } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import api from '@/lib/api';

import CMSAwardsPanel from '@/pages/admin/CMSAwardsPanel';
import CMSNewsPanel from '@/pages/admin/CMSNewsPanel';
import CMSOfficesPanel from '@/pages/admin/CMSOfficesPanel';
import CMSTestimonialsPanel from '@/pages/admin/CMSTestimonialsPanel';
import CMSContentPanel from '@/pages/admin/CMSContentPanel';
import CMSMediaPanel from '@/pages/admin/CMSMediaPanel';

const TAB_CONFIG = [
  { key: 'analytics', label: 'Analytics', icon: ChartLine, color: '#D4AF37' },
  { key: 'contacts', label: 'Contacts', icon: Users, color: '#0A1F3D' },
  { key: 'newsletters', label: 'Newsletter', icon: EnvelopeSimple, color: '#20A39E' },
  { key: 'careers', label: 'Careers', icon: Briefcase, color: '#6B46C1' },
  { key: 'partners', label: 'Partners', icon: Handshake, color: '#D4AF37' },
  { key: 'cms-awards', label: 'Awards', icon: Trophy, color: '#D4AF37' },
  { key: 'cms-news', label: 'News', icon: Newspaper, color: '#0A1F3D' },
  { key: 'cms-offices', label: 'Offices', icon: MapPin, color: '#20A39E' },
  { key: 'cms-testimonials', label: 'Reviews', icon: ChatCircle, color: '#6B46C1' },
  { key: 'cms-content', label: 'Content', icon: Gear, color: '#0A1F3D' },
  { key: 'cms-media', label: 'Media', icon: Image, color: '#D4AF37' },
];

/* ==================== LOGIN FORM ==================== */
function LoginForm({ onLogin }) {
  const [mode, setMode] = useState('password'); // password | otp-request | otp-verify | forgot | reset
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [otp, setOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);

  const handlePasswordLogin = async (e) => {
    e.preventDefault();
    setLoading(true); setError('');
    try {
      const res = await api.post('/admin/login', { email, password });
      if (res.data.success) onLogin(res.data.token);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Invalid email or password');
    } finally { setLoading(false); }
  };

  const handleSendOTP = async (purpose = 'login') => {
    setLoading(true); setError(''); setSuccess('');
    try {
      const endpoint = purpose === 'login' ? '/admin/send-otp' : '/admin/forgot-password';
      const res = await api.post(endpoint, { email });
      setSuccess('OTP sent to your email');
      setMode(purpose === 'login' ? 'otp-verify' : 'reset');
    } catch (err) {
      setError(err?.response?.data?.detail || 'Failed to send OTP');
    } finally { setLoading(false); }
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    setLoading(true); setError('');
    try {
      const res = await api.post('/admin/verify-otp', { email, otp });
      if (res.data.success) onLogin(res.data.token);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Invalid or expired OTP');
    } finally { setLoading(false); }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (newPassword.length < 6) { setError('Password must be at least 6 characters'); return; }
    setLoading(true); setError('');
    try {
      await api.post('/admin/reset-password', { email, otp, new_password: newPassword });
      setSuccess('Password reset successful! You can now login.');
      setMode('password');
      setOtp(''); setNewPassword('');
    } catch (err) {
      setError(err?.response?.data?.detail || 'Failed to reset password');
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-[#0A1F3D] flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-sm bg-white p-8"
      >
        <h1 data-testid="admin-login-title" className="text-2xl font-bold font-['Playfair_Display'] text-[#0A1F3D] mb-1">Admin Panel</h1>
        <p className="text-sm text-[#4A5568] mb-6">Osaka Group Dashboard</p>

        <AnimatePresence mode="wait">
          {/* PASSWORD LOGIN */}
          {mode === 'password' && (
            <motion.form key="password" initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 20 }} onSubmit={handlePasswordLogin}>
              <Input
                type="email"
                placeholder="Email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="admin-email-input"
                className="mb-3 rounded-none"
                required
              />
              <Input
                type="password"
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="admin-password-input"
                className="mb-4 rounded-none"
                required
              />
              {error && <p data-testid="admin-login-error" className="text-red-500 text-sm mb-3">{error}</p>}
              {success && <p className="text-green-600 text-sm mb-3">{success}</p>}
              <Button type="submit" data-testid="admin-login-btn" disabled={loading} className="w-full bg-[#D4AF37] text-[#0A1F3D] hover:bg-[#c5a033] rounded-none font-semibold mb-3">
                {loading ? 'Logging in...' : 'Login'}
              </Button>
              <div className="flex justify-between text-xs">
                <button type="button" onClick={() => { setMode('otp-request'); setError(''); setSuccess(''); }} data-testid="login-with-otp-btn" className="text-[#D4AF37] hover:underline font-medium">
                  Login with OTP
                </button>
                <button type="button" onClick={() => { setMode('forgot'); setError(''); setSuccess(''); }} data-testid="forgot-password-btn" className="text-[#4A5568] hover:text-[#0A1F3D]">
                  Forgot Password?
                </button>
              </div>
            </motion.form>
          )}

          {/* OTP REQUEST */}
          {mode === 'otp-request' && (
            <motion.div key="otp-req" initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 20 }}>
              <p className="text-sm text-[#4A5568] mb-4">We'll send a 6-digit OTP to your admin email.</p>
              <Input
                type="email"
                placeholder="Email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="otp-email-input"
                className="mb-4 rounded-none"
              />
              {error && <p className="text-red-500 text-sm mb-3">{error}</p>}
              <Button onClick={() => handleSendOTP('login')} data-testid="send-otp-btn" disabled={loading || !email} className="w-full bg-[#D4AF37] text-[#0A1F3D] hover:bg-[#c5a033] rounded-none font-semibold mb-3">
                {loading ? 'Sending OTP...' : 'Send OTP'}
              </Button>
              <button type="button" onClick={() => { setMode('password'); setError(''); }} className="flex items-center gap-1 text-xs text-[#4A5568] hover:text-[#0A1F3D]">
                <ArrowLeft size={12} /> Back to password login
              </button>
            </motion.div>
          )}

          {/* OTP VERIFY */}
          {mode === 'otp-verify' && (
            <motion.form key="otp-verify" initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 20 }} onSubmit={handleVerifyOTP}>
              <p className="text-sm text-[#4A5568] mb-1">Enter the 6-digit OTP sent to</p>
              <p className="text-sm font-bold text-[#0A1F3D] mb-4">{email}</p>
              <Input
                type="text"
                placeholder="Enter 6-digit OTP"
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                data-testid="otp-code-input"
                className="mb-4 rounded-none text-center text-xl tracking-[0.5em] font-bold"
                maxLength={6}
              />
              {error && <p className="text-red-500 text-sm mb-3">{error}</p>}
              {success && <p className="text-green-600 text-sm mb-3">{success}</p>}
              <Button type="submit" data-testid="verify-otp-btn" disabled={loading || otp.length !== 6} className="w-full bg-[#D4AF37] text-[#0A1F3D] hover:bg-[#c5a033] rounded-none font-semibold mb-3">
                {loading ? 'Verifying...' : 'Verify & Login'}
              </Button>
              <div className="flex justify-between text-xs">
                <button type="button" onClick={() => handleSendOTP('login')} className="text-[#D4AF37] hover:underline">Resend OTP</button>
                <button type="button" onClick={() => { setMode('password'); setError(''); setSuccess(''); }} className="text-[#4A5568] hover:text-[#0A1F3D]">Back to login</button>
              </div>
            </motion.form>
          )}

          {/* FORGOT PASSWORD */}
          {mode === 'forgot' && (
            <motion.div key="forgot" initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 20 }}>
              <div className="flex items-center gap-2 mb-4">
                <Key size={20} className="text-[#D4AF37]" />
                <span className="font-bold text-[#0A1F3D]">Reset Password</span>
              </div>
              <p className="text-sm text-[#4A5568] mb-4">Enter your admin email to receive a password reset OTP.</p>
              <Input
                type="email"
                placeholder="Email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="forgot-email-input"
                className="mb-4 rounded-none"
              />
              {error && <p className="text-red-500 text-sm mb-3">{error}</p>}
              <Button onClick={() => handleSendOTP('reset')} data-testid="send-reset-otp-btn" disabled={loading || !email} className="w-full bg-[#0A1F3D] text-white hover:bg-[#0f294d] rounded-none font-semibold mb-3">
                {loading ? 'Sending...' : 'Send Reset OTP'}
              </Button>
              <button type="button" onClick={() => { setMode('password'); setError(''); }} className="flex items-center gap-1 text-xs text-[#4A5568] hover:text-[#0A1F3D]">
                <ArrowLeft size={12} /> Back to login
              </button>
            </motion.div>
          )}

          {/* RESET PASSWORD */}
          {mode === 'reset' && (
            <motion.form key="reset" initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 20 }} onSubmit={handleResetPassword}>
              <div className="flex items-center gap-2 mb-4">
                <Key size={20} className="text-[#D4AF37]" />
                <span className="font-bold text-[#0A1F3D]">Set New Password</span>
              </div>
              <p className="text-sm text-[#4A5568] mb-4">Enter the OTP sent to <strong>{email}</strong></p>
              <Input
                type="text"
                placeholder="6-digit OTP"
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                data-testid="reset-otp-input"
                className="mb-3 rounded-none text-center text-xl tracking-[0.5em] font-bold"
                maxLength={6}
              />
              <Input
                type="password"
                placeholder="New password (min 6 chars)"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                data-testid="new-password-input"
                className="mb-4 rounded-none"
              />
              {error && <p className="text-red-500 text-sm mb-3">{error}</p>}
              {success && <p className="text-green-600 text-sm mb-3">{success}</p>}
              <Button type="submit" data-testid="reset-password-btn" disabled={loading || otp.length !== 6 || newPassword.length < 6} className="w-full bg-[#0A1F3D] text-white hover:bg-[#0f294d] rounded-none font-semibold mb-3">
                {loading ? 'Resetting...' : 'Reset Password'}
              </Button>
              <button type="button" onClick={() => { setMode('password'); setError(''); setSuccess(''); }} className="flex items-center gap-1 text-xs text-[#4A5568] hover:text-[#0A1F3D]">
                <ArrowLeft size={12} /> Back to login
              </button>
            </motion.form>
          )}
        </AnimatePresence>
      </motion.div>
    </div>
  );
}

/* ==================== SHARED COMPONENTS ==================== */
function StatsCard({ icon: Icon, label, value, color }) {
  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-white border border-slate-200 p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="w-10 h-10 flex items-center justify-center" style={{ backgroundColor: `${color}15` }}>
          <Icon size={22} style={{ color }} />
        </div>
        <span className="text-3xl font-bold font-['Playfair_Display'] text-[#0A1F3D]">{value}</span>
      </div>
      <p className="text-sm text-[#4A5568]">{label}</p>
    </motion.div>
  );
}

function DataTable({ data, columns, emptyMessage }) {
  if (!data.length) return <div className="text-center py-12 text-[#4A5568]">{emptyMessage || 'No submissions yet'}</div>;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-left text-sm" data-testid="admin-data-table">
        <thead>
          <tr className="border-b border-slate-200 bg-[#F5F5F5]">
            {columns.map((col) => (
              <th key={col.key} className="py-3 px-4 font-semibold text-[#0A1F3D] text-xs uppercase tracking-wider whitespace-nowrap">{col.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.map((row, i) => (
            <tr key={i} className="border-b border-slate-100 hover:bg-[#F5F5F5]/50 transition-colors">
              {columns.map((col) => (
                <td key={col.key} className="py-3 px-4 text-[#4A5568] max-w-[200px] truncate">
                  {col.render ? col.render(row[col.key], row) : (row[col.key] || '—')}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ==================== ANALYTICS PANEL ==================== */
function AnalyticsPanel({ analytics }) {
  if (!analytics) return <div className="text-center py-12 text-[#4A5568]">Loading analytics...</div>;
  const maxDailyViews = Math.max(...(analytics.daily || []).map(d => d.views), 1);
  const maxPageViews = Math.max(...(analytics.top_pages || []).map(p => p.views), 1);

  return (
    <div className="space-y-6" data-testid="analytics-panel">
      <div className="bg-white border border-slate-200 p-6">
        <h3 className="text-lg font-bold font-['Playfair_Display'] text-[#0A1F3D] mb-4">Daily Traffic (Last 30 Days)</h3>
        {analytics.daily?.length > 0 ? (
          <div className="flex items-end gap-1 h-40 overflow-x-auto" data-testid="daily-chart">
            {analytics.daily.map((day, i) => (
              <div key={i} className="flex flex-col items-center min-w-[28px] group relative">
                <div className="absolute -top-8 hidden group-hover:block bg-[#0A1F3D] text-white text-xs px-2 py-1 rounded whitespace-nowrap z-10">
                  {day.date}: {day.views} views, {day.visitors} visitors
                </div>
                <div className="w-5 bg-[#D4AF37] hover:bg-[#0A1F3D] transition-colors rounded-t cursor-pointer" style={{ height: `${Math.max((day.views / maxDailyViews) * 140, 4)}px` }} />
                <span className="text-[8px] text-[#4A5568] mt-1 rotate-[-45deg] origin-top-left whitespace-nowrap">{day.date.slice(5)}</span>
              </div>
            ))}
          </div>
        ) : <p className="text-[#4A5568] text-sm">No traffic data yet.</p>}
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bg-white border border-slate-200 p-6">
          <h3 className="text-lg font-bold font-['Playfair_Display'] text-[#0A1F3D] mb-4">Top Pages</h3>
          {analytics.top_pages?.length > 0 ? (
            <div className="space-y-3" data-testid="top-pages">
              {analytics.top_pages.map((page, i) => (
                <div key={i} className="flex items-center gap-3">
                  <span className="text-xs font-bold text-[#4A5568] w-5">{i + 1}</span>
                  <div className="flex-1">
                    <div className="flex justify-between items-center mb-1">
                      <span className="text-sm font-medium text-[#0A1F3D] truncate max-w-[200px]">{page.path}</span>
                      <span className="text-xs text-[#4A5568]">{page.views} views</span>
                    </div>
                    <div className="w-full bg-slate-100 h-1.5 rounded">
                      <div className="h-full bg-[#D4AF37] rounded" style={{ width: `${(page.views / maxPageViews) * 100}%` }} />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : <p className="text-[#4A5568] text-sm">No page data yet.</p>}
        </div>
        <div className="bg-white border border-slate-200 p-6">
          <h3 className="text-lg font-bold font-['Playfair_Display'] text-[#0A1F3D] mb-4">Top Referrers</h3>
          {analytics.top_referrers?.length > 0 ? (
            <div className="space-y-3" data-testid="top-referrers">
              {analytics.top_referrers.map((ref, i) => (
                <div key={i} className="flex justify-between items-center py-2 border-b border-slate-50">
                  <span className="text-sm text-[#4A5568] truncate max-w-[250px]">{ref.referrer}</span>
                  <span className="text-sm font-bold text-[#0A1F3D]">{ref.count}</span>
                </div>
              ))}
            </div>
          ) : <p className="text-[#4A5568] text-sm">No referrer data yet.</p>}
        </div>
      </div>

      <div className="bg-white border border-slate-200 p-6">
        <h3 className="text-lg font-bold font-['Playfair_Display'] text-[#0A1F3D] mb-4">Today's Traffic by Hour</h3>
        {analytics.hourly_today?.length > 0 ? (
          <div className="flex items-end gap-2 h-28" data-testid="hourly-chart">
            {Array.from({ length: 24 }, (_, h) => {
              const hourData = analytics.hourly_today.find(d => d.hour === h);
              const views = hourData?.views || 0;
              const maxH = Math.max(...analytics.hourly_today.map(d => d.views), 1);
              return (
                <div key={h} className="flex flex-col items-center flex-1 group relative">
                  <div className="absolute -top-7 hidden group-hover:block bg-[#0A1F3D] text-white text-xs px-2 py-1 rounded z-10">{h}:00 — {views} views</div>
                  <div className="w-full bg-[#20A39E] hover:bg-[#0A1F3D] transition-colors rounded-t cursor-pointer min-h-[2px]" style={{ height: `${views > 0 ? Math.max((views / maxH) * 100, 4) : 2}px` }} />
                  {h % 4 === 0 && <span className="text-[8px] text-[#4A5568] mt-1">{h}h</span>}
                </div>
              );
            })}
          </div>
        ) : <p className="text-[#4A5568] text-sm">No visits today yet.</p>}
      </div>
    </div>
  );
}

/* ==================== MAIN ADMIN PAGE ==================== */
export default function AdminPage() {
  const [token, setToken] = useState(() => sessionStorage.getItem('admin_token') || '');
  const [activeTab, setActiveTab] = useState('analytics');
  const [stats, setStats] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [data, setData] = useState({ contacts: [], newsletters: [], careers: [], partners: [] });
  const [loading, setLoading] = useState(false);

  const handleLogin = (t) => {
    setToken(t);
    sessionStorage.setItem('admin_token', t);
  };

  const handleLogout = () => {
    setToken('');
    sessionStorage.removeItem('admin_token');
  };

  const fetchData = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const [statsRes, contactsRes, newslettersRes, careersRes, partnersRes, analyticsRes] = await Promise.all([
        api.get(`/admin/stats?token=${token}`),
        api.get(`/admin/contacts?token=${token}`),
        api.get(`/admin/newsletters?token=${token}`),
        api.get(`/admin/careers?token=${token}`),
        api.get(`/admin/partners?token=${token}`),
        api.get(`/admin/analytics?token=${token}&days=30`),
      ]);
      setStats(statsRes.data);
      setAnalytics(analyticsRes.data);
      setData({
        contacts: contactsRes.data,
        newsletters: newslettersRes.data,
        careers: careersRes.data,
        partners: partnersRes.data,
      });
    } catch (err) {
      if (err?.response?.status === 401) handleLogout();
    } finally { setLoading(false); }
  }, [token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (!token) return <LoginForm onLogin={handleLogin} />;

  const formatDate = (val) => {
    if (!val) return '—';
    try { return new Date(val).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }); }
    catch { return val; }
  };

  const contactColumns = [
    { key: 'name', label: 'Name' }, { key: 'email', label: 'Email' }, { key: 'phone', label: 'Phone' },
    { key: 'subject', label: 'Subject' }, { key: 'message', label: 'Message' }, { key: 'created_at', label: 'Date', render: formatDate },
  ];
  const newsletterColumns = [
    { key: 'email', label: 'Email' }, { key: 'created_at', label: 'Subscribed On', render: formatDate },
  ];
  const careerColumns = [
    { key: 'name', label: 'Name' }, { key: 'email', label: 'Email' }, { key: 'phone', label: 'Phone' },
    { key: 'position', label: 'Position' }, { key: 'experience', label: 'Experience' }, { key: 'created_at', label: 'Date', render: formatDate },
  ];
  const partnerColumns = [
    { key: 'company_name', label: 'Company' }, { key: 'contact_person', label: 'Contact' }, { key: 'email', label: 'Email' },
    { key: 'phone', label: 'Phone' }, { key: 'partnership_type', label: 'Type' }, { key: 'message', label: 'Message' }, { key: 'created_at', label: 'Date', render: formatDate },
  ];

  const tabData = {
    contacts: { columns: contactColumns, data: data.contacts },
    newsletters: { columns: newsletterColumns, data: data.newsletters },
    careers: { columns: careerColumns, data: data.careers },
    partners: { columns: partnerColumns, data: data.partners },
  };

  return (
    <div className="min-h-screen bg-[#F5F5F5]">
      <header className="bg-[#0A1F3D] border-b border-white/10 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-4 flex items-center justify-between">
          <h1 data-testid="admin-dashboard-title" className="text-xl font-bold font-['Playfair_Display'] text-white">
            Osaka Group <span className="text-[#D4AF37]">Admin</span>
          </h1>
          <div className="flex items-center gap-4">
            <button onClick={fetchData} className="text-white/60 hover:text-white text-sm transition-colors">
              {loading ? 'Refreshing...' : 'Refresh'}
            </button>
            <button onClick={handleLogout} data-testid="admin-logout-btn" className="flex items-center gap-2 text-white/60 hover:text-red-400 text-sm transition-colors">
              <SignOut size={16} /> Logout
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 md:px-8 py-8">
        {analytics && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8" data-testid="admin-stats-grid">
            <StatsCard icon={ChartLine} label="Page Views (30d)" value={analytics.total_views} color="#D4AF37" />
            <StatsCard icon={Globe} label="Unique Visitors (30d)" value={analytics.unique_visitors} color="#20A39E" />
            <StatsCard icon={Users} label="Contact Submissions" value={stats?.total_contacts || 0} color="#0A1F3D" />
            <StatsCard icon={EnvelopeSimple} label="Newsletter Subs" value={stats?.total_newsletters || 0} color="#6B46C1" />
          </div>
        )}

        <div className="flex gap-1 mb-6 overflow-x-auto pb-2" data-testid="admin-tabs">
          {TAB_CONFIG.map((tab) => {
            let count = 0;
            if (tab.key === 'analytics') count = analytics?.total_views || 0;
            else if (!tab.key.startsWith('cms-')) count = tabData[tab.key]?.data?.length || 0;
            return (
              <button key={tab.key} onClick={() => setActiveTab(tab.key)} data-testid={`admin-tab-${tab.key}`}
                className={`flex items-center gap-2 px-4 py-2.5 text-xs font-medium whitespace-nowrap transition-colors ${
                  activeTab === tab.key ? 'bg-[#0A1F3D] text-white' : 'bg-white text-[#4A5568] hover:bg-slate-50 border border-slate-200'
                }`}>
                <tab.icon size={16} />
                {tab.label}
                {!tab.key.startsWith('cms-') && (
                  <span className={`text-xs px-2 py-0.5 rounded-full ${activeTab === tab.key ? 'bg-[#D4AF37] text-[#0A1F3D]' : 'bg-slate-100 text-[#4A5568]'}`}>
                    {count}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {activeTab === 'analytics' ? (
          <AnalyticsPanel analytics={analytics} />
        ) : activeTab === 'cms-awards' ? (
          <CMSAwardsPanel token={token} />
        ) : activeTab === 'cms-news' ? (
          <CMSNewsPanel token={token} />
        ) : activeTab === 'cms-offices' ? (
          <CMSOfficesPanel token={token} />
        ) : activeTab === 'cms-testimonials' ? (
          <CMSTestimonialsPanel token={token} />
        ) : activeTab === 'cms-content' ? (
          <CMSContentPanel token={token} />
        ) : activeTab === 'cms-media' ? (
          <CMSMediaPanel token={token} />
        ) : (
          <div className="bg-white border border-slate-200" data-testid="admin-table-container">
            <DataTable data={tabData[activeTab]?.data || []} columns={tabData[activeTab]?.columns || []} emptyMessage={`No ${activeTab} submissions yet`} />
          </div>
        )}
      </div>
    </div>
  );
}
